// FILE: src/components/ReadinessSections.js
//
// Above-the-fold sections of the redesigned Readiness page, laid out as a
// single three-column band to keep scrolling short:
//   ReadinessHeroPanel — gauge column: ring, status pill, trend, CTAs
//   FactorStack        — the six weighted inputs as compact stacked rows
//                        (explanations live below the fold in FactorGuide)
//   CoachingCard       — PM Pixel explains the biggest improvement opportunity
//   OpportunityPanel   — deep-dive on the highest-headroom metric
//
// All readiness math stays in readinessService / readinessPageHelpers; these
// components are presentation only.

import React from 'react';
import styles from './ReadinessPage.module.css';
import ProgressRing from '../shared/ProgressRing';
import CoachAvatar from '../coach/CoachAvatar';
import { STATUS_LABEL } from '../../services/readinessService';
import {
  METRIC_META, METRIC_ORDER, biggestOpportunity, coachMessage, toneFor,
} from './readinessPageHelpers';

function pillClass(status) {
  switch (status) {
    case 'exam_ready':          return styles.pillReady;
    case 'getting_close':       return styles.pillClose;
    case 'building_foundation': return styles.pillBuilding;
    default:                    return styles.pillStart;
  }
}

function barClass(value) {
  const tone = toneFor(value);
  if (tone === 'good') return styles.barGood;
  if (tone === 'warn') return styles.barWarn;
  return styles.barBrand;
}

export function ReadinessHeroPanel({
  readiness, trend, sessionCount, onStartPractice, onViewPlan,
}) {
  const score  = typeof readiness?.score === 'number' ? readiness.score : null;
  const status = readiness?.status || 'not_started';
  const trendValid = !!(trend && trend.sufficient && typeof trend.delta === 'number');
  const up   = trendValid && trend.direction === 'up';
  const down = trendValid && trend.direction === 'down';

  return (
    <section className={styles.hero} data-testid="readiness-headline">
      <ProgressRing value={score || 0} size={170} stroke={13}>
        <div className={styles.heroScore}>{score != null ? score : '—'}</div>
        <div className={styles.heroDenom}>/100</div>
      </ProgressRing>
      <span className={`${styles.pill} ${pillClass(status)}`}>
        {STATUS_LABEL[status] || 'Getting started'}
      </span>
      {trendValid && (
        <div className={`${styles.trendChip} ${up ? styles.trendUp : ''} ${down ? styles.trendDown : ''}`}>
          {up ? '↗' : down ? '↘' : '→'}{' '}
          {trend.delta > 0 ? '+' : ''}{trend.delta} pts vs last week
        </div>
      )}
      <p className={styles.heroSub}>
        Based on {sessionCount} session{sessionCount === 1 ? '' : 's'} since your baseline.
      </p>
      <div className={styles.heroCtas}>
        <button type="button" className={styles.btn}
          onClick={onStartPractice} data-testid="readiness-start-practice">
          Start Practice
        </button>
        <button type="button" className={styles.btnGhost}
          onClick={onViewPlan} data-testid="readiness-view-plan">
          View Study Plan
        </button>
      </div>
    </section>
  );
}

export function FactorStack({ readiness }) {
  const inputs = readiness?.inputs || {};
  return (
    <section className={styles.factorCard} data-testid="readiness-factors">
      <div className={styles.coachEyebrow}>What’s driving your score</div>
      <div className={styles.factorList}>
        {METRIC_ORDER.map((key) => {
          const meta = METRIC_META[key];
          const item = inputs[key];
          const value = typeof item?.value === 'number' ? Math.round(item.value) : null;
          const weightPct = Math.round((item?.weight || 0) * 100);
          return (
            <div key={key} className={styles.factorRow} data-testid={`readiness-input-${key}`}>
              <div className={styles.factorHead}>
                <span className={styles.factorTitle}>
                  <span className={styles.metricIcon} aria-hidden="true">{meta.icon}</span>
                  {meta.title}
                  <span className={styles.metricWeight}>{weightPct}%</span>
                </span>
                <span className={styles.factorVal}>
                  {value != null ? value : '—'}<span className={styles.metricDenom}>/100</span>
                </span>
              </div>
              <span className={styles.metricBar}>
                <span className={barClass(value || 0)} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function CoachingCard({ readiness, onStartPractice, onViewPlan }) {
  const opp = biggestOpportunity(readiness);
  const message = coachMessage(opp);
  const action = opp?.meta?.actions?.[0] || null;
  return (
    <aside className={styles.coachCard} data-testid="readiness-coaching">
      <div className={styles.coachEyebrow}>PM Pixel Coaching</div>
      <div className={styles.coachBody}>
        <CoachAvatar pose="study" heightPx={156} decorative />
        <p className={styles.coachSpeech}>{message}</p>
      </div>
      {action && (
        <div className={styles.coachTip}>
          <div className={styles.coachTipLbl}>Try this next</div>
          <div className={styles.coachTipMsg}>{action.label}</div>
          <button type="button" className={`${styles.btn} ${styles.btnSm}`}
            onClick={action.kind === 'plan' ? onViewPlan : onStartPractice}
            data-testid="readiness-coach-cta">
            {action.kind === 'plan' ? 'Open Study Plan' : 'Start Now'}
          </button>
        </div>
      )}
    </aside>
  );
}

export function OpportunityPanel({ readiness, weakPhases, onStartPractice, onViewPlan }) {
  const opp = biggestOpportunity(readiness);
  if (!opp) return null;
  return (
    <section className={styles.oppPanel} data-testid="readiness-opportunity">
      <div className={styles.oppMain}>
        <div className={styles.coachEyebrow}>Biggest Opportunity</div>
        <h3 className={styles.oppTitle}>
          <span aria-hidden="true">{opp.meta.icon}</span> {opp.meta.title}
          <span className={styles.oppGain}>up to +{opp.gain} pts</span>
        </h3>
        <p className={styles.oppWhy}>
          At {opp.value}/100 with a {opp.weightPct}% weight, this metric has more
          headroom than anything else on the board. {opp.meta.detail}
        </p>
        <div className={styles.oppActions}>
          {opp.meta.actions.map((a) => (
            <button key={a.label} type="button"
              className={`${styles.btnGhost} ${styles.btnSm}`}
              onClick={a.kind === 'plan' ? onViewPlan : onStartPractice}>
              {a.label} ›
            </button>
          ))}
        </div>
      </div>
      {Array.isArray(weakPhases) && weakPhases.length > 0 && (
        <div className={styles.oppWeak} data-testid="readiness-weak-phases">
          <div className={styles.coachEyebrow}>Weakest phases right now</div>
          <ul className={styles.weakList}>
            {weakPhases.map((w) => (
              <li key={w.phase} className={styles.weakItem}>
                <span className={styles.weakName}>{w.phase}</span>
                <span className={styles.weakAcc}>{w.accuracy}%</span>
                <span className={styles.weakSeen}>{w.correct} of {w.total}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
